import type {
  FetchScheduleSaveToLibrarySuggestionArgs,
  SaveScheduleEventToLibraryArgs,
  CreateScheduleWorkoutBatchArgs,
  CreateScheduleArgs,
  DeleteScheduleArgs,
  FetchScheduleArgs,
  FetchOrgScheduleArgs,
  OrgScheduleTrainer,
  EditScheduleArgs,
} from './types/schedule.types';
import type { ApiSchedule, UiSchedule } from '../schemas/schedule.schema';
import type { BaseApiPaginatedResponse } from './types/api.types';

import {
  createScheduleWorkoutBatchArgs,
  createScheduleArgs,
  mapScheduleFromApi,
} from './mappers/schedule.mappers';
import { destroy, patch, post, get } from './axios';

export const createEvent = async ({
  locale,
  organizationId,
  ...args
}: CreateScheduleArgs) => {
  // One request can target several clients/groups, so the backend always
  // answers with the array of rows it created — even for the common
  // single-client case. Every existing caller only used this response to
  // invalidate the calendar query, so the shape mismatch (typed as a single
  // `ApiSchedule`) went unnoticed; take the first created row for a caller
  // that needs the new event back (e.g. jumping straight into the player).
  const response = await post<ApiSchedule[]>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/schedule`,
    createScheduleArgs({ ...args, organizationId }),
  );
  const created = response?.data?.[0];
  if (!created) return null;
  return mapScheduleFromApi(created, locale);
};

// Batch add — unlike `createEvent`, the caller needs every created row, not
// just the first (a client jumping into a batch-added workout could land on
// any of the N tabs), so nothing here narrows the response to one element.
export const createScheduleWorkoutBatch = async ({
  locale,
  organizationId,
  ...args
}: CreateScheduleWorkoutBatchArgs) => {
  const response = await post<ApiSchedule[]>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/schedule/workouts/batch`,
    createScheduleWorkoutBatchArgs({ ...args, organizationId }),
  );
  return (response?.data ?? []).map((schedule) =>
    mapScheduleFromApi(schedule, locale),
  );
};

export const fetchEvents = async ({
  clientId,
  from,
  locale,
  organizationId,
  perspective,
  to,
  withMyScheduleOnly = false,
}: FetchScheduleArgs) => {
  let url = `/v2/users/${clientId}/schedules?from=${from}&to=${to}`;
  if (withMyScheduleOnly) {
    // F12 — 'client'/omitted is back-compat (today's response shape);
    // 'all' also returns trainer-side events with an additive `viewerRole`.
    //
    // T0a — `useSchedule` picks this branch on `clientId === me` ALONE, so it
    // serves the org self-view as well as the personal page. It used to drop
    // `organizationId`, `from`, and `to` here, which is what put personal and
    // other-organization rows on `/organizations/:orgId/schedule` and made
    // every calendar fetch read the account's whole history. The scope has to
    // travel with the request; the branch itself is correct (the org-scoped
    // client route resolves a self-request to `trainerId = me` and would drop
    // everything assigned to the caller as a client).
    const params = new URLSearchParams({ from, to });
    if (organizationId) params.set('organizationId', organizationId);
    if (perspective) params.set('perspective', perspective);
    url = `/v2/auth/me/schedules?${params.toString()}`;
  } else if (organizationId) {
    // BE ruta je organizations/:orgId/clients/:userId/schedules (users/… ne postoji → 404)
    url = `/v2/organizations/${organizationId}/clients/${clientId}/schedules?from=${from}&to=${to}`;
  }
  const response = await get<BaseApiPaginatedResponse<ApiSchedule[]>>(url);
  return {
    ...response,
    data: response?.data?.map((item) =>
      mapScheduleFromApi(item, locale),
    ) as UiSchedule[],
  };
};

export const fetchOrgSchedule = async ({
  from,
  locale,
  organizationId,
  to,
}: FetchOrgScheduleArgs) => {
  const response = await get<{
    data: ApiSchedule[];
    trainers: OrgScheduleTrainer[];
  }>(`/v2/organizations/${organizationId}/schedule?from=${from}&to=${to}`);
  return {
    data: (response?.data ?? []).map((item) =>
      mapScheduleFromApi(item, locale),
    ) as UiSchedule[],
    trainers: response?.trainers ?? [],
  };
};

export const editEvent = async ({
  id,
  locale,
  organizationId,
  ...args
}: EditScheduleArgs) => {
  const response = await patch<ApiSchedule>(
    organizationId
      ? `/v2/organizations/${organizationId}/schedule/${id}`
      : `/v2/schedule/${id}`,
    createScheduleArgs({ ...args, organizationId }),
  );
  if (!response?.data) return null;
  return mapScheduleFromApi(response.data, locale);
};

export const fetchScheduleSaveToLibrarySuggestion = async ({
  eventId,
  organizationId,
}: FetchScheduleSaveToLibrarySuggestionArgs) => {
  const response = await get<{ data: { title: string } }>(
    organizationId
      ? `/v2/organizations/${organizationId}/schedule/${eventId}/save-to-library-suggestion`
      : `/v2/schedule/${eventId}/save-to-library-suggestion`,
  );
  return response?.data ?? null;
};

export const saveScheduleEventToLibrary = async ({
  eventId,
  organizationId,
  title,
}: SaveScheduleEventToLibraryArgs) => {
  const response = await post<{ id: string; title: string }>(
    organizationId
      ? `/v2/organizations/${organizationId}/schedule/${eventId}/save-to-library`
      : `/v2/schedule/${eventId}/save-to-library`,
    { title },
  );
  return response?.data ?? null;
};

export const deleteEvent = async ({
  id,
  organizationId,
  removeProgram,
}: DeleteScheduleArgs) => {
  if (removeProgram) {
    await destroy(
      organizationId
        ? `/v2/organizations/${organizationId}/schedule/remove-program/${id}`
        : `/v2/schedule/remove-program/${id}`,
    );
  } else {
    await destroy(
      organizationId
        ? `/v2/organizations/${organizationId}/schedule/${id}`
        : `/v2/schedule/${id}`,
    );
  }
};
